import { Box, styled, Typography } from '@mui/material';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import { useLayoutContext } from 'contexts/LayoutContext';
import { UploadType } from 'further-types/files';
import { FileType } from 'further-ui/utils';
import downloadXlxs from 'utils/downloadXlxs';
import useFileUpload, { UploadFileResult } from 'hooks/ui/useFileUpload';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import TransactionPreview from './TransactionPreview';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { Column } from 'further-ui/components/Layout';

const DownloadButton = styled(Button)({
  padding: 0,
  textDecoration: 'underline',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

type Props = {
  uploadedFile?: UploadFileResult;
  onFileUpload: (file: UploadFileResult | null) => void;
  onNextStep: () => void;
  bankAccountId: string;
};

const UploadFile: React.FC<Props> = ({
  onFileUpload,
  uploadedFile,
  onNextStep,
  bankAccountId,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();

  const [bankAccounts] = api.bankAccount.listBankAccounts.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  const bankAccount = bankAccounts.find(
    (account) => account.id === bankAccountId,
  );

  const [data] = api.bankStatement.getMostRecentOperation.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
    bankAccountId,
  });

  const downloadExampleFile = api.bankStatement.downloadExample.useMutation({
    onSuccess: (data) => {
      const currentDate = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      const filename = `Bank statement upload template - ${
        bankAccount?.accountName || 'Account'
      } - ${vehicle?.vehicleName} - ${currentDate}`;

      downloadXlxs(data.file, filename);
    },
  });

  const handleFileUpload = async (files: Array<UploadedFile>) => {
    if (!files?.[0]?.path) return;
    const uploadFileResult = await uploadFile(
      files[0],
      UploadType.LpBankStatements,
    );
    if (uploadFileResult.data) {
      onFileUpload(uploadFileResult.data);
    } else {
      error(uploadFileResult.error.message);
    }
  };

  const handleDownloadExample = () => {
    downloadExampleFile.mutate();
  };

  const handleSubmit = () => {
    if (!uploadedFile) return;

    onNextStep();
  };

  return (
    <Column spacing="lg">
      <Column spacing="xs">
        <Typography variant="h4">Prepare file for upload</Typography>
        <Typography variant="body1" color="grey.600">
          Download a template statement file for copying your bank statement
          entries into. It is best to download your bank statements in CSV so
          you can copy them into the upload file format.
        </Typography>
        <Box>
          <DownloadButton
            variant="text"
            onClick={handleDownloadExample}
            loading={downloadExampleFile.isPending}
          >
            Click here to download our upload template
          </DownloadButton>
        </Box>
      </Column>
      <Column spacing="sm">
        <Typography variant="h4">Most recent transaction</Typography>
        <TransactionPreview {...(data ?? {})} />
      </Column>
      <Column spacing="sm">
        <Typography variant="h4">File to upload</Typography>
        <Dropzone
          name="file"
          maxFiles={1}
          accept={{
            [FileType.CSV]: ['.csv'],
            [FileType.Excel]: ['.xls'],
            [FileType.ExcelAlt]: ['.xlsx'],
          }}
          onDropFile={handleFileUpload}
          files={
            uploadedFile
              ? [
                  {
                    preview: uploadedFile.url,
                    fileName: uploadedFile.name ?? uploadedFile.url,
                  },
                ]
              : []
          }
          onRemove={() => {
            onFileUpload(null);
          }}
        />
        <Typography variant="body2" color="textSecondary">
          File should end in .XLS, .XLSX or .CSV.
        </Typography>
      </Column>
      <ButtonGroup>
        <Button
          variant="contained"
          disabled={!uploadedFile}
          onClick={handleSubmit}
          size="large"
        >
          Next
        </Button>
      </ButtonGroup>
    </Column>
  );
};

export default UploadFile;
